import type { WorkItem } from '../types';

/** Client-side "Order by" for the work-item board/list. Sorting happens on the
 *  already-fetched page (no extra queries), so it's cheap and never mutates the
 *  stored `sort_order`. `manual` keeps the server order (sort_order, then oldest first). */
export type OrderKey = 'manual' | 'priority' | 'due_date' | 'start_date' | 'created' | 'modified';

export interface WorkItemOrder {
  key: OrderKey;
  dir: 'asc' | 'desc';
}

export const DEFAULT_ORDER: WorkItemOrder = { key: 'manual', dir: 'asc' };

export const ORDER_OPTIONS: { key: OrderKey; label: string }[] = [
  { key: 'manual', label: 'Manual' },
  { key: 'priority', label: 'Priority' },
  { key: 'due_date', label: 'Due date' },
  { key: 'start_date', label: 'Start date' },
  { key: 'created', label: 'Created' },
  { key: 'modified', label: 'Updated' },
];

// Severity, most-urgent first (used for `asc`).
const PRIORITY_RANK: Record<string, number> = {
  urgent: 0,
  high: 1,
  medium: 2,
  low: 3,
  none: 4,
};

// Items with no date sort last regardless of direction.
const NO_DATE = Number.POSITIVE_INFINITY;

function sortValue(item: WorkItem, key: OrderKey): number {
  switch (key) {
    case 'priority':
      return PRIORITY_RANK[item.priority] ?? 99;
    case 'due_date':
      return item.due_date ? Date.parse(item.due_date) : NO_DATE;
    case 'start_date':
      return item.start_date ? Date.parse(item.start_date) : NO_DATE;
    case 'created':
      return Date.parse(item.created);
    case 'modified':
      return Date.parse(item.modified);
    default:
      return 0;
  }
}

/** Return a new array sorted per `order`. `manual` returns the input unchanged
 *  (server order). Ties break by work-item number for stability. */
export function sortWorkItems(items: WorkItem[], order: WorkItemOrder): WorkItem[] {
  if (order.key === 'manual') return items;
  const dir = order.dir === 'asc' ? 1 : -1;
  return [...items].sort((a, b) => {
    const av = sortValue(a, order.key);
    const bv = sortValue(b, order.key);
    // Undated items always last, in both directions.
    if (av === NO_DATE && bv === NO_DATE) return a.number - b.number;
    if (av === NO_DATE) return 1;
    if (bv === NO_DATE) return -1;
    const delta = av - bv;
    return (delta !== 0 ? delta : a.number - b.number) * dir;
  });
}
